import classnames from 'classnames'
import styles from './Clock.module.scss'
import { useContext, useEffect, useRef, useState } from 'react'
import gsap from 'gsap'
import { GlobalSettingsContext } from '@/context/GlobalSettings'
import useWindowResize from '@/hooks/use-window-resize'

const TIME_ZONES = {
  ATLANTIC: {
    title: 'Atlantic',
    offset: '+4:00',
    zone: 'America/St_Johns',
  },
  EASTERN: {
    title: 'Eastern',
    offset: '+5:00',
    zone: 'America/New_York',
  },
  CENTRAL: {
    title: 'Central',
    offset: '+6:00',
    zone: 'America/Chicago',
  },
  MOUNTAIN: {
    title: 'Mountain',
    offset: '+7:00',
    zone: 'America/Denver',
  },
  PACIFIC: {
    title: 'Pacific',
    offset: '+8:00',
    zone: 'America/Los_Angeles',
  },
  ALASKA: {
    title: 'Alaska',
    offset: '+9:00',
    zone: 'America/Nome',
  },
}

const Clock = ({ className }: { className?: string }) => {
  const intervalRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const { navigationData } = useContext(GlobalSettingsContext)
  const [time, setTime] = useState<{ hour: string | number; minutes: string | number; amPm: 'PM' | 'AM' } | null>(null)
  const colonRef = useRef<HTMLSpanElement | null>(null)
  const timelineRef = useRef<GSAPTimeline | null>(null)
  const resizeKey = useWindowResize()

  useEffect(() => {
    if (!navigationData?.location?.timeZone) return

    const fire = () => {
      const timestamp = new Date().toLocaleDateString('en-GB', {
        timeZone: TIME_ZONES[navigationData?.location.timeZone as keyof typeof TIME_ZONES].zone,
        hour: '2-digit',
        minute: '2-digit',
      })

      const hourMinutesString = timestamp.split(' ')[1]
      const hour = parseInt(hourMinutesString.split(':')[0])
      const amPm = hour >= 12 ? 'PM' : 'AM'
      const hourAmPm = hour > 12 ? hour - 12 : hour
      let minutes: string | number = parseInt(hourMinutesString.split(':')[1])
      minutes = minutes >= 10 ? minutes : `0${minutes}`
      setTime({ hour: hourAmPm, minutes, amPm })
    }

    fire()

    intervalRef.current = setInterval(() => {
      fire()
    }, 5000)

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [navigationData?.location.timeZone])

  useEffect(() => {
    if (timelineRef.current) {
      timelineRef.current.kill()
    }

    timelineRef.current = gsap.timeline({
      repeat: -1,
    })

    timelineRef.current.fromTo(
      colonRef.current,
      {
        autoAlpha: 0,
      },
      {
        autoAlpha: 1,
        duration: 0.01,
      },
    )
    timelineRef.current.fromTo(
      colonRef.current,
      {
        autoAlpha: 1,
      },
      {
        autoAlpha: 0,
        duration: 0.01,
      },
      '+=1',
    )
    timelineRef.current.fromTo(
      colonRef.current,
      {
        autoAlpha: 0,
      },
      {
        autoAlpha: 0,
        duration: 0.01,
      },
      '+=1',
    )
  }, [resizeKey, time])

  if (time === null) return null

  return (
    <span className={classnames(styles.Clock, className)}>
      <span className={styles.time}>
        <span className={styles.time__hour}>( {time.hour}</span>
        <span
          className={styles.time__color}
          ref={colonRef}
        >
          :
        </span>
        <span className={styles.time__minutes}>
          {time.minutes}&nbsp;
          {time.amPm} )
        </span>
      </span>
    </span>
  )
}

Clock.displayName = 'Clock'

export default Clock
